import { z } from "zod";
import { actorSchema, confidenceLabelSchema, idSchema, instantSchema } from "@amber/contracts";
import { scoreSchema } from "./shared.js";

export const NAMING_DECISIONS = ["assigned", "ambiguous", "unresolved"] as const;
export const namingDecisionSchema = z.enum(NAMING_DECISIONS);

export const PROFILE_DECISIONS = ["matched", "ambiguous", "no_match", "not_applicable"] as const;
export const profileDecisionSchema = z.enum(PROFILE_DECISIONS);

export const CALIBRATION_STATUSES = ["uncalibrated", "calibrated", "out_of_scope", "stale", "unavailable"] as const;
export const calibrationStatusSchema = z.enum(CALIBRATION_STATUSES);

export const SEMANTIC_VALIDATION_OUTCOMES = ["supported", "contradicted", "insufficient_evidence"] as const;
export const semanticValidationOutcomeSchema = z.enum(SEMANTIC_VALIDATION_OUTCOMES);

export const speakerAssignmentRecordSchema = z.strictObject({
  id: idSchema("asg"),
  speaker_id: idSchema("spk"),
  result_id: idSchema("res"),
  naming: z
    .strictObject({
      status: namingDecisionSchema,
      name: z.string().min(1).nullable(),
      confidence: scoreSchema.nullable(),
      confidence_label: confidenceLabelSchema,
      evidence: z.array(z.string().min(1)),
    })
    .refine((n) => n.status !== "assigned" || n.name !== null, "an assigned name is present"),
  profile: z
    .strictObject({
      decision: profileDecisionSchema,
      profile_id: idSchema("prf").nullable(),
      confidence: scoreSchema.nullable(),
      confidence_label: confidenceLabelSchema,
      reason: z.string().min(1).nullable(),
      alternatives: z.array(idSchema("prf")),
    })
    .refine((p) => (p.decision === "matched") === (p.profile_id !== null), "profile_id is set exactly when matched")
    .refine((p) => p.decision === "matched" || p.reason !== null, "an unmatched profile decision has a reason"),
  unit_attribution_confidence: scoreSchema.nullable(),
  unit_attribution_label: confidenceLabelSchema,
  calibration_status: calibrationStatusSchema,
  confirmed_by_person: z
    .strictObject({ by: actorSchema, at: instantSchema, correction_id: idSchema("cor").nullable() })
    .nullable(),
  semantic_validation: z
    .strictObject({ outcome: semanticValidationOutcomeSchema, checked_at: instantSchema, method: z.string().min(1) })
    .nullable(),
});
export type SpeakerAssignmentRecord = z.infer<typeof speakerAssignmentRecordSchema>;
